/**
 * Reconhecer — mensagem do reconhecimento (REC-33 a REC-41, REC-56).
 *
 * A etapa da mensagem oferece duas mensagens prontas e o texto livre. Em #UAU só o texto
 * livre vale: é o reconhecimento mais valioso, e a justificativa precisa ser de quem envia.
 *
 * Módulo puro de propósito: é a única fonte dos textos prontos e dos limites de tamanho, e
 * existe para ser testado sem React.
 */
import { CoinKey } from './recognition-points';

/** Mínimo de caracteres da mensagem, sem contar os emojis. */
export const MIN_MESSAGE_LENGTH = 50;

/** Máximo de caracteres da mensagem, contando os emojis. */
export const MAX_MESSAGE_LENGTH = 600;

/** Contagem do `TextEditor`: `textLength` sem emojis, `fullLength` com eles. */
export type MessageLength = { textLength: number; fullLength: number };

export type MessageContent = { html: string; length: MessageLength };

export type PresetId = 1 | 2;

export const FREE_TEXT_OPTION = 3;

export type MessageOption = PresetId | typeof FREE_TEXT_OPTION;

export type MessageLengthIssue = 'too-short' | 'too-long';

export const MESSAGE_PRESETS: { id: PresetId; text: string }[] = [
  { id: 1, text: 'Obrigado(a) pela parceria e colaboração. Sua contribuição foi importante para o resultado do time.' },
  { id: 2, text: 'Agradeço pelo apoio e pela disposição em colaborar. Você fez a diferença nesta entrega.' },
];

/** O mesmo HTML que o editor produz quando a pessoa digita o texto: o campo `message` não muda de formato. */
function paragraphHtml(text: string) {
  return `<p>${text}</p>\n`;
}

function presetText(id: PresetId) {
  return MESSAGE_PRESETS.find(preset => preset.id === id)?.text ?? '';
}

export function presetHtml(id: PresetId) {
  return paragraphHtml(presetText(id));
}

/** As mensagens prontas não valem para #UAU. */
export function coinAllowsPresets(coin?: CoinKey) {
  return coin !== 'uau';
}

export function measureText(text: string): MessageLength {
  const length = text.replace(/\s+/g, ' ').trim().length;

  return { textLength: length, fullLength: length };
}

/** Mensagem que não veio do editor (rascunho recuperado): mede o texto, e não as tags do HTML. */
export function contentFromHtml(html: string): MessageContent {
  const holder = document.createElement('div');
  holder.innerHTML = html;

  return { html, length: measureText(holder.textContent || '') };
}

/**
 * Opção a que uma mensagem já gravada pertence. Um rascunho só guarda o HTML, então a opção
 * é deduzida dele: igual a uma mensagem pronta (e fora do #UAU) é a própria opção; qualquer
 * outro texto é texto livre.
 */
export function messageOptionFor(message: string | undefined, coin?: CoinKey): MessageOption | undefined {
  if (!message) return undefined;

  const preset = coinAllowsPresets(coin) ? MESSAGE_PRESETS.find(({ id }) => presetHtml(id) === message) : undefined;

  return preset ? preset.id : FREE_TEXT_OPTION;
}

/** A mensagem que vale para o envio, dadas a opção escolhida e o texto livre digitado. */
export function resolveMessage(
  option: MessageOption | undefined,
  freeText: MessageContent | undefined,
  coin?: CoinKey,
): MessageContent | undefined {
  if (!coinAllowsPresets(coin) || option === FREE_TEXT_OPTION) return freeText;
  if (option === undefined) return undefined;

  return { html: presetHtml(option), length: measureText(presetText(option)) };
}

/** O problema de tamanho a avisar. Campo vazio não é avisado: só não deixa seguir. */
export function messageLengthIssue(length?: MessageLength): MessageLengthIssue | undefined {
  if (!length || length.fullLength === 0) return undefined;
  if (length.fullLength > MAX_MESSAGE_LENGTH) return 'too-long';
  if (length.textLength < MIN_MESSAGE_LENGTH) return 'too-short';

  return undefined;
}

export function isMessageValid(content?: MessageContent) {
  if (!content || !content.html) return false;

  return content.length.textLength >= MIN_MESSAGE_LENGTH && content.length.fullLength <= MAX_MESSAGE_LENGTH;
}
